// @flow
import * as React from 'react';
import cn from 'classnames';
import * as Yup from 'yup';
// eslint-disable-next-line import/named
import { FormattedMessage, type IntlShape } from 'react-intl';

// Components
import { Formik, Form, FormikActions } from 'formik';
import { TextField, FormikField } from 'components/api';
import { Button } from 'components/atoms';

// Types
import { gtmUserEventType } from 'types/gtm';

import './ForgotPassword.scss';

// Utils
import { isObjEmpty } from 'utils/varValidation';

type Props = {
  className?: string,
  forgotPassword: (username: string, email: string) => Object,
  close: () => void,
  gtmUserEvent: gtmUserEventType,
  intl: IntlShape
};

type Values = {
  username: string,
  email: string
};

const validationSchema = () => {
  return Yup.object().shape({
    username: Yup.string().required(),
    email: Yup.string()
      .required()
      .email()
  });
};

export default class ForgotPassword extends React.Component<Props> {
  static block = 'forgot-password';

  constructor(props: Props) {
    super(props);
    (this: any).onSubmit = this.onSubmit.bind(this);
    (this: any).formikForm = React.createRef();
    (this: any).userNameInput = React.createRef();
    (this: any).emailInput = React.createRef();
  }

  initialValues = {
    username: '',
    email: ''
  };
  formikForm: React.Ref<any>;
  userNameInput: React.Ref<'input'>;
  emailInput: React.Ref<'input'>;

  async onSubmit(values: Values, actions: FormikActions<Values>) {
    (this: any).userNameInput.current.blur();
    (this: any).emailInput.current.blur();
    const { username, email } = values;
    const { setSubmitting, setStatus, resetForm } = actions;
    const { forgotPassword, close, gtmUserEvent } = this.props;
    const data = await forgotPassword(username, email);
    const success = data && data.success;
    setSubmitting(false);
    setStatus({ success });
    if (success) {
      resetForm(this.initialValues);
      close();
    }
    gtmUserEvent(
      'Reset Password Button',
      'Clicked',
      success ? 'Email successfully sent!' : 'Invalid username or email',
      success ? 'Forgot Password Modal > Sign In Page' : 'Forgot Password Modal'
    );
  }

  resetForm() {
    const formikForm = (this: any).formikForm.current;
    formikForm.handleReset();
  }

  render() {
    const me = this;
    return (
      <Formik
        initialValues={this.initialValues}
        validationSchema={validationSchema()}
        onSubmit={this.onSubmit}
        ref={this.formikForm}
        render={({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          isSubmitting,
          dirty,
          status
        }) => {
          const { className, intl } = this.props;
          const ns = ForgotPassword.block;
          const classProps = cn(ns, className);
          const disabled = !dirty || !isObjEmpty(errors);
          const errorClassProps = cn(`${ns}__error-msg text-center`, {
            visible: status && !status.success,
            invisible: !status || status.success
          });
          return (
            <Form className={classProps} onSubmit={handleSubmit}>
              <FormattedMessage id="c8.common.forgot.password">
                {text => (
                  <div className={`${ns}__title text-center`}>{text}</div>
                )}
              </FormattedMessage>

              <FormattedMessage id="c8.forgot.password.we.ll.email.your.password">
                {text => (
                  <div className={`${ns}__subtitle text-center`}>{text}</div>
                )}
              </FormattedMessage>

              <FormattedMessage id="c8.common.invalid.username.or.email">
                {text => <div className={errorClassProps}>{text}</div>}
              </FormattedMessage>

              <div className={`${ns}__form-fields`}>
                <FormattedMessage id="c8.common.username">
                  {text => (
                    <FormikField
                      renderedComponent={TextField}
                      name="username"
                      placeholder={text}
                      htmlRef={me.userNameInput}
                      tabIndex={0}
                      aria-label={intl.formatMessage({
                        id: 'c8.aria.forgot.password.username.input',
                        defaultMessage:
                          'Enter username to recover your password'
                      })}
                    />
                  )}
                </FormattedMessage>

                <FormattedMessage id="c8.common.email">
                  {text => (
                    <FormikField
                      renderedComponent={TextField}
                      name="email"
                      type="email"
                      placeholder={text}
                      htmlRef={me.emailInput}
                      tabIndex={0}
                      aria-label={intl.formatMessage({
                        id: 'c8.aria.forgot.password.email.input',
                        defaultMessage: 'Enter email to recover your password'
                      })}
                    />
                  )}
                </FormattedMessage>
              </div>
              <Button
                type="Submit"
                size="medium"
                disabled={disabled}
                className={`${ns}__submit-btn d-flex`}
                tabIndex={0}
              >
                {disabled ? (
                  <FormattedMessage id="c8.forgot.password.enter.details">
                    {msg => msg}
                  </FormattedMessage>
                ) : (
                  <FormattedMessage id="c8.forgot.password.reset.password">
                    {msg => msg}
                  </FormattedMessage>
                )}
                {isSubmitting && <div className="ld ld-ring ld-spin-fast" />}
              </Button>
            </Form>
          );
        }}
      />
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/pages/User/SignIn/components/ForgotPassword/ForgotPassword.js